import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';

export type TokenUsageScope = 'me' | 'org' | 'user';

export interface TokenUsagePeriod {
  start: string;
  end: string;
}

export interface TokenUsageMetrics {
  input_tokens: number;
  standard_input_tokens: number;
  cache_creation_input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cached_input_tokens: number;
  reasoning_tokens: number;
  audio_input_tokens: number;
  audio_output_tokens: number;
  input_image_tokens: number;
  output_image_tokens: number;
  turn_count: number;
  model_call_count: number;
}

export interface TokenUsageDailyEntry extends TokenUsageMetrics {
  date: string;
}

export interface TokenUsageModelEntry extends TokenUsageMetrics {
  model: string;
}

export interface TokenUsageUserEntry extends TokenUsageMetrics {
  user_id: string | null;
}

export interface TokenUsageProviderEntry extends TokenUsageMetrics {
  provider: string;
}

export interface TokenUsageAgentEntry extends TokenUsageMetrics {
  agent_id: string;
}

export interface TokenUsageOperationEntry extends TokenUsageMetrics {
  operation: string;
}

export interface TokenUsageQueryRow extends TokenUsageMetrics {
  dimensions: {
    operation?: string;
  };
}

export interface TokenUsageQueryResponse {
  scope: string;
  user_id: string | null;
  org_id: string | null;
  period: TokenUsagePeriod;
  group_by: string[];
  rows: TokenUsageQueryRow[];
}

export interface TokenUsageBreakdowns {
  daily: TokenUsageDailyEntry[];
  by_model: TokenUsageModelEntry[];
  by_provider?: TokenUsageProviderEntry[];
  by_agent?: TokenUsageAgentEntry[];
  by_user?: TokenUsageUserEntry[];
}

export interface TokenUsageResponse {
  scope: string;
  user_id: string | null;
  org_id: string | null;
  period: TokenUsagePeriod;
  totals: TokenUsageMetrics;
  breakdowns: TokenUsageBreakdowns;
}

export interface WindowUsage {
  key: string;
  name: string;
  type: 'hourly' | 'daily';
  percent: number;
  resets_at?: string;
}

export interface WindowLimitResponse {
  organization_id: string;
  user_id: string;
  windows: WindowUsage[];
}

export interface GetTokenUsageParams {
  startDate: string;
  endDate: string;
  scope: TokenUsageScope;
  userId?: string;
}

export const getTokenUsage = async (
  config: ConfigurationSettings,
  params: GetTokenUsageParams
): Promise<TokenUsageResponse> => {
  const searchParams = new URLSearchParams({
    start_date: params.startDate,
    end_date: params.endDate,
  });

  const basePath = config.paths.seliseAi.tokenUsage;
  const path =
    params.scope === 'org'
      ? `${basePath}/org`
      : params.scope === 'user' && params.userId
        ? `${basePath}/users/${params.userId}`
        : `${basePath}/me`;

  const url = `${config.paths.seliseAiBaseUrl}${path}?${searchParams.toString()}`;

  return clients.get<TokenUsageResponse>(url, config.blocksKey);
};

/** How full each limit window that holds for this user is. An empty userId reads the organization's. */
export const getWindowLimit = async (
  config: ConfigurationSettings,
  userId: string
): Promise<WindowLimitResponse> => {
  const base = config.paths.klaxServiceBaseUrl.replace(/\/$/, '');
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  const query = params.toString();
  const url = `${base}${config.paths.windowLimit}${query ? `?${query}` : ''}`;
  return clients.get<WindowLimitResponse>(url, config.blocksKey);
};

export const queryTokenUsageByOperation = async (
  config: ConfigurationSettings,
  params: GetTokenUsageParams
): Promise<TokenUsageOperationEntry[]> => {
  const url = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tokenUsage}/query`;
  const response = await clients.post<TokenUsageQueryResponse>(
    url,
    config.blocksKey,
    JSON.stringify({
      scope: params.scope,
      user_id: params.userId,
      start_date: params.startDate,
      end_date: params.endDate,
      runtime_status: 'all',
      group_by: ['operation'],
      sort_by: 'total_tokens',
    })
  );

  return (response.rows ?? [])
    .filter(
      (row) =>
        row.dimensions.operation && (row.total_tokens > 0 || row.model_call_count > 0)
    )
    .map(({ dimensions, ...metrics }) => ({
      operation: dimensions.operation as string,
      ...metrics,
    }));
};
